import { ActionIcon, Button, Card, Grid, Group, Select, Stack, Text, Textarea, TextInput, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type { FileRefDto, RequiredDocDto } from '@mexp/shared';
import { FileUploader } from '@/features/files/FileUploader';
import { useDepartments, useStaffLookup } from '@/features/shared/lookups';

export interface TaskRow {
  key: string;
  department: string | null;
  assigneeId: string | null;
  title: string;
  requiredDocKey: string | null;
  instructions: string;
  attachments: FileRefDto[];
}

export const emptyTaskRow = (): TaskRow => ({
  key: Math.random().toString(36).slice(2),
  department: null,
  assigneeId: null,
  title: '',
  requiredDocKey: null,
  instructions: '',
  attachments: [],
});

export function taskRowErrors(row: TaskRow): string[] {
  const errors: string[] = [];
  if (!row.department) errors.push('department');
  if (!row.assigneeId) errors.push('person');
  if (!row.title.trim()) errors.push('document');
  return errors;
}

/** The person's department proposes the target date from their task, and RA accepts it; none is set here. */
export function taskRowsToPayload(rows: TaskRow[]) {
  return rows.map((r) => ({
    department: r.department as string,
    assigneeId: r.assigneeId as string,
    title: r.title.trim(),
    requiredDocKey: r.requiredDocKey ?? undefined,
    instructions: r.instructions.trim() || undefined,
    attachmentFileIds: r.attachments.map((f) => f.id),
  }));
}

function AssigneeSelect({ department, value, onChange, error }: { department: string | null; value: string | null; onChange: (v: string | null) => void; error?: string }) {
  const people = useStaffLookup({ department }, !!department);
  const all = useStaffLookup({}, !!department && people.isSuccess && people.data.length === 0);
  // Emails tell apart people who share a name (e.g. an imported account and a newer one).
  const options = (people.data?.length ? people.data : (all.data ?? [])).map((u) => ({ value: u.id, label: `${u.fullName} · ${u.email}` }));
  return (
    <Select
      label="Person"
      placeholder={department ? 'Choose who provides it' : 'Choose a department first'}
      data={options}
      value={value}
      onChange={onChange}
      searchable
      disabled={!department}
      withAsterisk
      error={error}
      description={department && people.isSuccess && !people.data.length ? 'Nobody is in this department yet; showing all staff' : undefined}
      nothingFoundMessage="Nobody matches"
    />
  );
}

/** Editable list of document tasks (department → person → document → due date). */
export function TaskRowsEditor({
  rows,
  onChange,
  requiredDocs,
  showErrors,
  onBusyChange,
}: {
  rows: TaskRow[];
  onChange: (rows: TaskRow[]) => void;
  requiredDocs: RequiredDocDto[] | { key: string; name: string }[];
  showErrors?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) {
  const departments = useDepartments();
  const update = (key: string, patch: Partial<TaskRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const docOptions = requiredDocs.map((d) => ({ value: d.key, label: d.name }));

  return (
    <Stack gap="md">
      {rows.map((row, index) => {
        const errors = showErrors ? taskRowErrors(row) : [];
        return (
          <Card key={row.key} padding="md" radius="md" bg="var(--mantine-color-default-hover)">
            <Stack gap="sm">
              <Group justify="space-between">
                <Text fw={600} fz="sm">
                  Task {index + 1}
                </Text>
                <Tooltip label="Remove this task">
                  <ActionIcon color="red" onClick={() => onChange(rows.filter((r) => r.key !== row.key))} aria-label={`Remove task ${index + 1}`}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label="Department"
                    data={(departments.data ?? []).map((d) => ({ value: d.code, label: d.name }))}
                    value={row.department}
                    onChange={(department) => update(row.key, { department, assigneeId: null })}
                    searchable
                    withAsterisk
                    error={errors.includes('department') ? 'Choose a department' : undefined}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <AssigneeSelect
                    department={row.department}
                    value={row.assigneeId}
                    onChange={(assigneeId) => update(row.key, { assigneeId })}
                    error={errors.includes('person') ? 'Choose a person' : undefined}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    label="Document to provide"
                    placeholder="e.g. Finished product COA for the last 3 batches"
                    value={row.title}
                    onChange={(e) => update(row.key, { title: e.currentTarget.value })}
                    withAsterisk
                    error={errors.includes('document') ? 'Describe the document' : undefined}
                  />
                </Grid.Col>
                {docOptions.length > 0 && (
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label="Covers checklist item"
                      placeholder="Optional"
                      data={docOptions}
                      value={row.requiredDocKey}
                      onChange={(requiredDocKey) => {
                        const doc = docOptions.find((d) => d.value === requiredDocKey);
                        update(row.key, { requiredDocKey, title: row.title || doc?.label || '' });
                      }}
                      clearable
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={12}>
                  <Textarea
                    label="Instructions"
                    placeholder="Anything the person should know (optional)"
                    autosize
                    minRows={1}
                    maxRows={4}
                    value={row.instructions}
                    onChange={(e) => update(row.key, { instructions: e.currentTarget.value })}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <FileUploader
                    area="wi_attachment"
                    value={row.attachments}
                    onChange={(attachments) => update(row.key, { attachments })}
                    label="Reference files for this task (optional)"
                    onBusyChange={onBusyChange}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>
        );
      })}
      <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => onChange([...rows, emptyTaskRow()])} w="fit-content">
        Add a task
      </Button>
    </Stack>
  );
}
